import {
  ContentfulInformation,
  DxlInformation,
  DxlInformationPeriod,
  DxlInformationPrice,
  Product,
} from '@/types/Product.types';

import formatCurrency from './formatCurrency';

type PickPriceProps = Required<Pick<Product, 'dxlInformation'>>['dxlInformation']['price'] | undefined;

export const retrievePrice = (price: PickPriceProps, type: DxlInformationPrice['name']) =>
  price?.find(item => item.name === type)?.price ?? 0;

export const retrievePriceByTypeOrFirstOne = (price: PickPriceProps, type: DxlInformationPrice['name']) => {
  const priceByType = price?.find(item => item.name === type)?.price;
  if (!priceByType) {
    return price?.length && price.length > 0 ? price[0].price : 0;
  }
  return priceByType;
};

export const pickCostOne = (price: PickPriceProps) => price?.find(item => item?.name === 'Cost1');
export const pickCostTwo = (price: PickPriceProps) => price?.find(item => item?.name === 'Cost2');

export const costOnePrice = (price: PickPriceProps) => formatCurrency(price ? pickCostOne(price)?.price : 0);
export const costTwoPrice = (price: PickPriceProps) => formatCurrency(price ? pickCostTwo(price)?.price : 0);

export const costOneUnit = (price: PickPriceProps) => price && pickCostOne(price)?.unit;
export const costTwoUnit = (price: PickPriceProps) => price && pickCostTwo(price)?.unit;

/**
 * Default contract option: longest minimumServiceLength wins, MONTHLY breaks ties,
 * so prices are comparable across editions regardless of API order.
 * When allowedUuids is non-empty, only those periods are considered.
 */
export const pickLongestPeriod = (
  periods: DxlInformationPeriod[] | undefined,
  allowedUuids?: string[]
): DxlInformationPeriod | undefined => {
  if (!periods?.length) return undefined;
  const allowed = allowedUuids?.length ? periods.filter(period => allowedUuids.includes(period.uuid)) : [];
  const candidates = allowed.length ? allowed : periods;

  return candidates.reduce((best, period) => {
    const bestLength = best.minimumServiceLength ?? 0;
    const length = period.minimumServiceLength ?? 0;
    if (length !== bestLength) return length > bestLength ? period : best;
    return best.type !== 'MONTHLY' && period.type === 'MONTHLY' ? period : best;
  });
};

export const lowestEditionPrice = (
  data: DxlInformation | undefined,
  contentfulData: ContentfulInformation | undefined
) => {
  let editionsPrices;
  if (contentfulData) {
    const contentfulEditions = contentfulData.editionIds;

    if (contentfulEditions) {
      const contentfulEditionsTrimmed = contentfulEditions.map(edition => edition.trim());
      const filteredEditions = data?.editions?.filter(edition => contentfulEditionsTrimmed.includes(edition.id));
      editionsPrices = filteredEditions?.map(edition => edition?.price?.filter(item => item.name === 'Cost1')[0]);
    }
  }

  if (!editionsPrices) {
    editionsPrices = data?.editions?.map(edition => edition?.price?.filter(item => item.name === 'Cost1')[0]);
  }

  if (editionsPrices) {
    const prices = editionsPrices.filter(item => item && item.price > 0).map(item => item.price);
    if (prices.length > 0) {
      return Math.min(...prices);
    } else {
      return 0;
    }
  } else {
    return 0;
  }
};

export const getCurrencySymbol = () => {
  const OPCO = process.env.GATSBY_OPCO;
  //Placeholder function for when using in portugal and other opcos
  return '€';
};
